import React, { useState, useEffect, useCallback } from 'react';
import TaskInventory, { Translations as TaskInventoryTranslations } from '../task-inventory/task-inventory';
import { Inventory, TaskInventory as TaskInventoryType, WayPoint, CustomUnitsDefinition, Scan } from '@bringg/types';
import { Collapse, Card } from 'antd';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import CustomModal from '../custom-modal/custom-modal';
import TaskInventoryForm from '../task-inventory/task-inventory-form/task-inventory-form';
import ExpandCollapseButton from '../expand-collapse-button/expand-collapse-button';
import TaskInventoriesSearch from '../task-inventories-search/task-inventories-search';
import {
	getAllTaskInventoryChildren,
	getTaskInventoryTotals,
	getAllChildTaskInventoryIds,
	getAllTaskInventoryInWayPoint,
	getClosedItemChildren,
	isClosing,
	handleShownFields
} from './utils/task-inventories-utils';
import SpinnerWrapper from '../spinner/spinner';
import { TaskInventoriesFlagsAccess } from 'features/waypoint-card/waypoint-card.consts';
const { Panel } = Collapse;

export interface Translations extends TaskInventoryTranslations {}

interface AllTaskInventoriesInWayPoint {
	[key: number]: TaskInventoryType;
}
interface TaskInventoriesProps {
	taskInventories: TaskInventoryType[];
	translations: Translations;
	onQuantityChange?: (massQuantityUpdate: TaskInventoryType[]) => void;
	onInventoriesChange?: (inventories: Inventory[]) => void;
	editable?: boolean;
	depth?: number;
	bordered?: boolean;
	flagsAccess: TaskInventoriesFlagsAccess;
	wayPointData?: WayPoint;
	allInventoriesInTask?: TaskInventoryType[];
	customUnitsDefinition?: CustomUnitsDefinition;
	taskInventoryScanMap?: Map<number, Scan>;
	doNotMultipleWeightByQuantity?: boolean;
	shouldShowLinkSourceTask?: boolean;
}
const Loader = () => <SpinnerWrapper className="task-inventories-spinner" />;
const VerticalLine = () => <span className="vertical-line"> </span>;

const EXPAND_LOADER_THRESHOLD: number = 25;

const TaskInventories: React.FC<TaskInventoriesProps> = ({
	taskInventories,
	onQuantityChange,
	onInventoriesChange,
	translations,
	editable = true,
	depth = 0,
	bordered = true,
	flagsAccess,
	wayPointData,
	allInventoriesInTask,
	customUnitsDefinition,
	taskInventoryScanMap,
	doNotMultipleWeightByQuantity,
	shouldShowLinkSourceTask
}: TaskInventoriesProps) => {
	const [taskInventoryToEdit, setTaskInventoryToEdit] = useState<TaskInventoryType>();

	const { isOpen: isModalOpen, open: openModal, close: closeModal } = CustomModal.useModalState(false);

	const [expanded, setExpanded] = useState(false);
	const [expandKey, setExpandKey] = useState({ activeKey: [] });
	const [filteredInventories, setFilteredInventories] = useState<TaskInventoryType[]>(taskInventories);
	const [parentTaskInventoryIds, setParentTaskInventoryIds] = useState<string[]>([]);
	const [isLoadingCollapse, setIsLoadingCollapse] = useState(false);

	const [shownFields, setShownFields] = useState({
		scan_string: false,
		weight: false,
		dimensions: false,
		price: false,
		note: false,
		scheduled_arrival: false
	});
	useEffect(() => {
		setParentTaskInventoryIds(() => getAllChildTaskInventoryIds(taskInventories));
	}, []);

	useEffect(() => {
		setParentTaskInventoryIds(() => getAllChildTaskInventoryIds(filteredInventories));
	}, [filteredInventories]);

	useEffect(() => {
		if (parentTaskInventoryIds?.length > EXPAND_LOADER_THRESHOLD) {
			setIsLoadingCollapse(true);
		} else {
			changeActiveKeyWithTaskInventoriesIds();
		}
	}, [expanded]);
	useEffect(() => {
		setFilteredInventories(taskInventories);
		handleShownFields(allTaskInventoriesInWayPoint, shownFields, setShownFields, customUnitsDefinition);
	}, [taskInventories]);

	useEffect(() => {
		if (isLoadingCollapse) {
			changeActiveKeyWithTaskInventoriesIds();
		}
	}, [isLoadingCollapse]);

	const changeActiveKeyWithTaskInventoriesIds = () => {
		const taskInventoriesIds: string[] = expanded && !expandKey.activeKey.length ? parentTaskInventoryIds : [];
		changeActiveKey(taskInventoriesIds);
	};

	const toggleExpandCollapse = () => {
		setExpanded(prevState => !prevState);
	};

	const changeActiveKey = useCallback(keys => {
		//get children of the item that is being closed for handling items with open children when closing parent
		const shouldGetClosedItemChildren: boolean =
			expandKey.activeKey.length && keys.length && isClosing(keys, expandKey.activeKey);
		const closedItemChildren =
			shouldGetClosedItemChildren && getClosedItemChildren(expandKey.activeKey, keys, taskInventories);

		if (closedItemChildren.length) {
			keys = keys.filter(item => !closedItemChildren.includes(item));
		}

		setExpandKey({
			activeKey: keys
		});
		if (!keys.length && expanded) {
			setExpanded(false);
		}
		setIsLoadingCollapse(false);
	}, []);

	const allTaskInventoriesInWayPoint: AllTaskInventoriesInWayPoint = getAllTaskInventoryInWayPoint(
		allInventoriesInTask,
		wayPointData.id
	);

	const onEditCLick = (event, taskInventory) => {
		event.stopPropagation();
		setTaskInventoryToEdit(taskInventory);
		openModal();
	};

	const hideForm = () => {
		closeModal();
	};

	const showEditButton: boolean =
		editable &&
		(flagsAccess.enable_dispatcher_edit_task_inventory_quantity || flagsAccess.enable_dimension_editing_in_ui);

	const getTaskInventoryCard = (taskInventory: TaskInventoryType) => {
		return (
			<Card
				className={`task-inventories-card task-inventory-row ${taskInventory.id}`}
				bordered={bordered}
				key={taskInventory.id.toString()}
				extra={
					showEditButton && (
						<span onClick={event => onEditCLick(event, taskInventory)}>
							<BringgIcon
								iconName={BringgFontIcons.Pencil}
								className="pencil"
								data-test-id="task-inventories-card"
							/>
						</span>
					)
				}
			>
				<TaskInventory
					parentTaskInventory={taskInventory}
					translations={translations}
					editable={editable}
					shownFields={shownFields}
					getAllTaskInventoryChildren={getAllTaskInventoryChildren}
					getTaskInventoryTotals={getTaskInventoryTotals}
					customUnitsDefinition={customUnitsDefinition}
					taskInventoryScanMap={taskInventoryScanMap}
					doNotMultipleWeightByQuantity={doNotMultipleWeightByQuantity}
					shouldShowLinkSourceTask={shouldShowLinkSourceTask}
				/>
			</Card>
		);
	};

	const getTaskInventoryCollapse = (taskInventory: TaskInventoryType, depth: number) => {
		return (
			<Collapse
				key={taskInventory.id.toString()}
				style={{ marginLeft: `${depth * 3}%` }}
				className={`site-collapse-custom-collapse task-inventories-collapse task-inventory-row taskinventoryid${taskInventory.id}`}
				onChange={changeActiveKey}
				activeKey={expandKey?.activeKey}
			>
				<Panel
					key={taskInventory.id.toString()}
					className="site-collapse-custom-panel"
					header={
						<TaskInventory
							parentTaskInventory={taskInventory}
							translations={translations}
							editable={editable}
							shownFields={shownFields}
							getAllTaskInventoryChildren={getAllTaskInventoryChildren}
							getTaskInventoryTotals={getTaskInventoryTotals}
							customUnitsDefinition={customUnitsDefinition}
							taskInventoryScanMap={taskInventoryScanMap}
							doNotMultipleWeightByQuantity={doNotMultipleWeightByQuantity}
							shouldShowLinkSourceTask={shouldShowLinkSourceTask}
						/>
					}
					extra={
						depth === 0 &&
						showEditButton && (
							<span onClick={event => onEditCLick(event, taskInventory)}>
								<BringgIcon
									iconName={BringgFontIcons.Pencil}
									className="pencil"
									data-test-id="site-collapse-custom"
								/>
							</span>
						)
					}
				>
					{getTaskInventoryChildren(taskInventory.inventories, 1)}
				</Panel>
			</Collapse>
		);
	};

	const getTaskInventoryChildren = (taskInventoryChildren: TaskInventoryType[], depth) => {
		return taskInventoryChildren.map(taskInventoryChild => {
			if (taskInventoryChild.inventories?.length) {
				return getTaskInventoryCollapse(taskInventoryChild, depth);
			}
			return (
				<TaskInventory
					depth={depth}
					key={taskInventoryChild.id.toString()}
					parentTaskInventory={taskInventoryChild}
					translations={translations}
					editable={editable}
					shownFields={shownFields}
					getAllTaskInventoryChildren={getAllTaskInventoryChildren}
					customUnitsDefinition={customUnitsDefinition}
					taskInventoryScanMap={taskInventoryScanMap}
					doNotMultipleWeightByQuantity={doNotMultipleWeightByQuantity}
					shouldShowLinkSourceTask={shouldShowLinkSourceTask}
				/>
			);
		});
	};

	const taskInventoriesList = filteredInventories.map(taskInventory => {
		if (taskInventory.inventories?.length) {
			return getTaskInventoryCollapse(taskInventory, 0);
		}
		return getTaskInventoryCard(taskInventory);
	});
	const setFilteredInventoriesFromSearch = (taskInventories: TaskInventoryType[], shouldExpand: boolean) => {
		setExpanded(() => shouldExpand);
		setFilteredInventories(taskInventories);
	};

	const taskInventoriesActions = () => {
		return taskInventories.length ? (
			<div className="task-inventories-actions">
				{
					<ExpandCollapseButton
						onClick={toggleExpandCollapse}
						isExpanded={!!expandKey.activeKey.length}
						translations={translations}
						isDisabled={!filteredInventories.length || !parentTaskInventoryIds?.length}
					/>
				}
				<VerticalLine />
				<TaskInventoriesSearch
					taskInventories={taskInventories}
					allTaskInventoriesInWayPoint={allTaskInventoriesInWayPoint}
					getAllTaskInventoryChildren={getAllTaskInventoryChildren}
					setFilteredInventories={setFilteredInventoriesFromSearch}
					changeActiveKey={changeActiveKey}
					translations={translations}
				/>
			</div>
		) : (
			''
		);
	};
	return (
		!!taskInventories.length && (
			<div className="task-inventories-container">
				{taskInventoriesActions()}
				<div className="task-inventories-content">
					<div className="task-inventories">
						{isLoadingCollapse ? <Loader /> : taskInventoriesList}

						<CustomModal
							className="task-inventory-edit-modal"
							isOpen={isModalOpen}
							onClickOutside={hideForm}
						>
							<h1>{translations.editItem}</h1>
							<TaskInventoryForm
								taskInventory={taskInventoryToEdit}
								parentTaskInventory={taskInventoryToEdit}
								taskInventories={taskInventories}
								onQuantityChange={onQuantityChange}
								onInventoriesChange={onInventoriesChange}
								onSave={hideForm}
								onCancel={hideForm}
								translations={translations}
								flagsAccess={flagsAccess}
								customUnitsDefinition={customUnitsDefinition}
							/>
						</CustomModal>
					</div>
				</div>
			</div>
		)
	);
};

export default TaskInventories;
